import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  ALLOWED_VEHICLE_PHOTO_TYPES,
  useUploadVehiclePhoto,
  type Vehicle,
} from '@/routes/fleet/useFleet';

/**
 * A vehicle's photo, and the way to replace it (M1-44). M1-27 shipped the two
 * endpoints behind this with no caller at all; this is the caller.
 *
 * ⚠️ **The current photo stays on screen while a replacement uploads**, and
 * that is the point rather than a nicety. `RequestPhotoUpload` deliberately
 * leaves a `Ready` vehicle `Ready` — so `photoUrl` keeps being served — and a
 * screen that blanked the image on the first click would throw that away and
 * show nothing at all if the upload were then abandoned. The server half is
 * pinned by `RequestUpload_WhileAPhotoIsAlreadyReady_KeepsTheCurrentPhotoServable`;
 * this is the half a person can see.
 */
export function VehiclePhotoPanel({
  vehicle,
  canManage,
  onAnnounce,
}: {
  vehicle: Vehicle;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  // ⚠️ The OUTCOME is announced, not only the start. Failure already reaches a
  // screen reader through `role="alert"` below; success changed text in a
  // plain <div> and said nothing at all, so the one outcome that went
  // unannounced was the good one — leaving silence indistinguishable from a
  // hang. Found in the review of #113.
  const upload = useUploadVehiclePhoto(vehicle.id, (outcome) =>
    onAnnounce(
      outcome === 'done'
        ? t('fleet.photo.announceDone', { label: vehicle.label })
        : t('fleet.photo.announceFailed'),
    ),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = upload.phase === 'uploading' || upload.phase === 'processing';

  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared immediately so choosing the SAME file twice fires `change` again
    // — after a failure that is exactly what somebody would do.
    event.target.value = '';
    if (file) {
      onAnnounce(t('fleet.photo.announceStarted', { name: file.name }));
      upload.upload(file);
    }
  }

  // What the SERVER last recorded, which is a different question from what
  // this tab is doing right now. Both can be true at once: a vehicle whose
  // stored status is `Failed` while a fresh upload is in flight.
  const stored = vehicle.photoStatus;

  return (
    <section aria-labelledby="vehicle-photo-heading" className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 id="vehicle-photo-heading" className="text-lg font-semibold">
          {t('fleet.photo.heading')}
        </h2>
        {canManage ? (
          <>
            {/* ⚠️ Hidden from the accessibility tree AND taken out of the tab
                order, because the Button beside it is the real control: it
                carries the label, the disabled state and the click. Caught by
                the axe scan already on this page, which flagged the input as
                unlabelled — but a label alone would have left a second,
                invisible tab stop for one action. `aria-hidden` without
                `tabIndex={-1}` trades this violation for `aria-hidden-focus`,
                so it takes both.

                ⚠️ `TechnicianPhotoUpload` (M1-24) wraps its input in a <label>
                instead, which satisfies axe and leaves that double tab stop in
                place. Not changed here — out of M1-44's scope — but it is the
                same bug. */}
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_VEHICLE_PHOTO_TYPES.join(',')}
              className="sr-only"
              id="vehicle-photo-input"
              data-testid="vehicle-photo-input"
              aria-hidden="true"
              tabIndex={-1}
              onChange={onFileChosen}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {vehicle.photoUrl ? t('fleet.photo.replace') : t('fleet.photo.add')}
            </Button>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        {vehicle.photoUrl ? (
          <img
            src={vehicle.photoUrl}
            // Named, not decorative: on this page the photo IS the subject,
            // unlike the fleet list where the label sits right beside it.
            alt={t('fleet.photo.alt', { label: vehicle.label })}
            className="h-32 w-44 rounded-md border object-cover"
            data-testid="vehicle-photo"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-32 w-44 items-center justify-center rounded-md border border-dashed text-xs text-[var(--color-muted-foreground)]"
          >
            {t('fleet.photo.none')}
          </div>
        )}

        <div className="space-y-1 text-sm" data-testid="vehicle-photo-state">
          {/* ⚠️ Each of the four stored states says something different, and
              `Failed` says what to DO. "Something went wrong" is the one
              message nobody can act on, which is why the hook carries a
              reason rather than a boolean. */}
          {busy ? (
            <p>
              {upload.phase === 'uploading'
                ? t('fleet.photo.uploading', { name: upload.fileName ?? '' })
                : t('fleet.photo.processing')}
              {/* Said out loud only when there is something to lose: a
                  replacement in flight over a photo already on screen. */}
              {vehicle.photoUrl ? (
                <span className="block text-xs text-[var(--color-muted-foreground)]">
                  {t('fleet.photo.currentStaysUntilReady')}
                </span>
              ) : null}
            </p>
          ) : upload.phase === 'failed' ? (
            <div role="alert" className="space-y-1">
              <p className="font-medium text-[var(--color-destructive)]">
                {t('fleet.photo.uploadFailed')}
              </p>
              <p className="text-[var(--color-muted-foreground)]">
                {t(`fleet.photo.failure.${upload.failure ?? 'unknown'}`)}
              </p>
              <div className="flex gap-2 pt-1">
                {upload.canRetry ? (
                  <Button type="button" variant="outline" size="sm" onClick={upload.retry}>
                    {t('fleet.photo.tryAgain')}
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" size="sm" onClick={upload.dismiss}>
                  {t('fleet.photo.dismiss')}
                </Button>
              </div>
            </div>
          ) : stored === 'Ready' ? (
            <p className="text-[var(--color-muted-foreground)]">{t('fleet.photo.ready')}</p>
          ) : stored === 'Pending' ? (
            // Not an in-flight upload — this tab is idle. A stored `Pending`
            // means a previous attempt asked for a URL and never confirmed, so
            // the honest reading is "unfinished", not "working on it".
            <p className="text-[var(--color-muted-foreground)]">{t('fleet.photo.pending')}</p>
          ) : stored === 'Failed' ? (
            <div className="space-y-1">
              <p className="font-medium text-[var(--color-destructive)]">
                {t('fleet.photo.storedFailedTitle')}
              </p>
              <p className="text-[var(--color-muted-foreground)]">
                {t('fleet.photo.storedFailedNext')}
              </p>
            </div>
          ) : (
            <p className="text-[var(--color-muted-foreground)]">{t('fleet.photo.noneExplains')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
